import { useCallback } from 'react';

import { useCanvasSelectionCircles, useCanvasUpdateHandler } from '.';
import { useApplianceContext } from '../../applianceContext';
import { useDrawingShapeContext } from '../../drawingShapeContext';
import { useSlabImgContext } from '../../slabImgContext';

import { drawingTool } from '~/core/engine/DrawingTool';
import { ColorModel, DrawingShape } from '~/core/models';
import { drawSideItems } from '~/core/utils';
import { isCanvasContextValid, isCanvasSafe } from '~/core/utils/canvasSafety';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { selectFabricCanvas } from '~/redux/slices/fabric';

export const useDrawingOperations = () => {
  const dispatch = useAppDispatch();

  const { isDraggingAppliance } = useApplianceContext();
  const slabImg = useSlabImgContext();

  const { updateShapesDimensions, updateShapesTexts, onDimensionEdit } =
    useCanvasUpdateHandler();
  const { renderSelectionCircles } = useCanvasSelectionCircles();

  const {
    getters: { drawingShapes, activeDrawingShape },
  } = useDrawingShapeContext();

  const selectedColor = useAppSelector(selectSelectedColor);
  const canvas = useAppSelector(selectFabricCanvas);

  /**
   * Clears the canvas and updates shape dimensions
   */
  const clearAndUpdateShapes = useCallback(
    (canvas: fabric.Canvas) => {
      if (!isCanvasSafe(canvas)) {
        return false;
      }

      drawingTool.clearCanvas({ canvas, preserveAppliances: true });
      updateShapesDimensions();

      return true;
    },
    [updateShapesDimensions]
  );

  /**
   *  Updates text elements and draws side items
   */
  const updateTextsAndArea = useCallback(
    (overlayColor: string) => {
      const renameSubscriptions = updateShapesTexts(overlayColor);

      drawSideItems({
        drawingShapes,
        selectedColor,
        canvas,
        activeDrawingShape,
        onDimensionEdit,
      });

      return { renameSubscriptions };
    },
    [
      updateShapesTexts,
      drawingShapes,
      selectedColor,
      canvas,
      activeDrawingShape,
      onDimensionEdit,
    ]
  );

  /**
   * Fills the canvas with colors and renders selection circles
   */
  const fillCanvasAndRender = useCallback(
    (
      canvas: fabric.Canvas,
      selectedColor: ColorModel,
      drawingShapes: DrawingShape[]
    ) => {
      if (!isCanvasSafe(canvas)) {
        return false;
      }

      drawingTool.fillColor(
        canvas,
        selectedColor,
        drawingShapes,
        isDraggingAppliance,
        slabImg.element
      );

      renderSelectionCircles();

      return true;
    },
    [isDraggingAppliance, slabImg.element, renderSelectionCircles]
  );

  /**
   * Safely renders the canvas with additional safety checks
   */
  const safeRenderCanvas = useCallback((canvas: fabric.Canvas) => {
    requestAnimationFrame(() => {
      if (isCanvasSafe(canvas)) {
        try {
          if (isCanvasContextValid(canvas)) {
            canvas.renderAll();
          }
        } catch (error) {
          console.warn('Error in canvas renderAll:', error);
        }
      }
    });
  }, []);

  /**
   * Completes the drawing operation and updates state
   */
  const finalizeDrawing = useCallback(() => {
    dispatch(updateNeedRedrawCanvas(false));
  }, [dispatch]);

  return {
    clearAndUpdateShapes,
    updateTextsAndArea,
    fillCanvasAndRender,
    safeRenderCanvas,
    finalizeDrawing,
  };
};
